"use client";

import { MantineProvider } from "@mantine/core";
import { DatesProvider } from "@mantine/dates";
import { Notifications } from "@mantine/notifications";
import { MedplumProvider } from "@medplum/react-hooks";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type JSX, type ReactNode, useEffect, useState } from "react";
import { setMedplumCookie } from "@/actions/auth/set-medplum-cookie";
import { SessionExpiredBanner } from "@/components/auth/SessionExpiredBanner";
import { AuthProvider } from "@/context/AuthContext";
import { createLogger } from "@/lib/logger";
import { createMedplumClientSideClient } from "@/lib/medplum/clientside";
import { contrastSafeColorVariables, theme } from "../../app/theme";
import { AuthSessionCoordinator } from "./AuthSessionCoordinator";
import { DialogProvider } from "./DialogProvider";
import { PostHogProvider } from "./PostHogProvider";

const logger = createLogger("AppProviders");
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

export default function AppProviders({ children }: { children: ReactNode }): JSX.Element {
  const [medplum] = useState(() => createMedplumClientSideClient());

  // MedplumProvider reads the profile on its first render and starts to listen for changes in its
  // own effect. `/auth/me` can answer between the two (locally it nearly always does), and then
  // the provider keeps `profile` undefined for the life of the page. `useSubscription` reads that
  // as signed out, so every realtime hook on it (inbox, chat, care team) never subscribes. A parent
  // runs its effects after its children, so the provider listens by now - announce the profile
  // once more. Not `change`: the subscription manager reconnects its socket on that event.
  useEffect(() => {
    if (medplum.getProfile()) medplum.dispatchEvent({ type: "profileRefreshed" });
  }, [medplum]);

  useEffect(() => {
    const syncSessionCookie = async () => {
      try {
        const activeLogin = medplum.getActiveLogin();
        if (activeLogin) {
          await setMedplumCookie(activeLogin);
        }
      } catch (error) {
        logger.warn("Failed to synchronize Medplum session cookie", { error });
      }
    };

    // Browser storage restores before effects run, so sync as early as we can — but don't hold
    // the tree back waiting for it (see the render comment below).
    void syncSessionCookie();
    medplum.addEventListener("profileRefreshed", syncSessionCookie);
    return () => {
      medplum.removeEventListener("profileRefreshed", syncSessionCookie);
    };
  }, [medplum]);

  return (
    <QueryClientProvider client={queryClient}>
      {/*
        Pin the color scheme. There is no dark mode — every surface, token, and SCSS module here is
        light-only — but Mantine's default manager reads `localStorage["mantine-color-scheme-value"]`,
        so one stray `dark` in a browser silently flips every scheme-keyed Mantine rule while our own
        styles stay light. That shipped a real bug: `Alert`'s message is the one Mantine element whose
        color is chosen by scheme (`--mantine-color-black` light / `--mantine-color-white` dark), so a
        failed fax rendered its red "Fax failed" title over white-on-pink body text — the reason was in
        the DOM and invisible on screen. `forceColorScheme` ignores localStorage and the OS entirely.
      */}
      <MantineProvider
        theme={theme}
        forceColorScheme="light"
        cssVariablesResolver={contrastSafeColorVariables}
      >
        {/*
          Weeks start Sunday. Two libraries disagree on this by default and nothing used to
          reconcile them: dayjs's `en` locale uses `weekStart: 0`, so every `startOf("week")` here
          is Sunday, while `@mantine/dates` defaults `firstDayOfWeek` to 1 (Monday). So the
          calendar grid rendered Sunday-first and every month dropdown rendered Monday-first, on
          the same screen. Set it once — a per-call-site `firstDayOfWeek` only fixes the pickers
          that exist today, and the next one added would arrive Monday-first again.
        */}
        <DatesProvider settings={{ firstDayOfWeek: 0 }}>
          <PostHogProvider>
            <MedplumProvider medplum={medplum}>
              {/*
                `children` renders unconditionally. Gating it on the cookie sync withheld every
                route's RSC payload until an effect resolved, then mounted it a render later — and
                `/` is an async server component whose top-level branch is `redirect()`, so the
                delayed mount broke the router (React #310 / "This page couldn't load"). The sync
                below still runs; it just no longer blocks the tree.
              */}
              <AuthSessionCoordinator>
                <AuthProvider>
                  <DialogProvider>
                    <Notifications />
                    <SessionExpiredBanner />
                    {children}
                  </DialogProvider>
                </AuthProvider>
              </AuthSessionCoordinator>
            </MedplumProvider>
          </PostHogProvider>
        </DatesProvider>
      </MantineProvider>
    </QueryClientProvider>
  );
}
